/** @format */
import axios from 'axios';
import { Request, Response } from 'express';
import React, { useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { MarketCode, MarketSlug, ukMarket } from '@toucan/client/global/market';

import { Error500 } from '@toucan/client/views/error-500';

type ErrorPageProps = {
    /**
     * The current page url
     */
    path: string;
    /**
     * The error
     */
    error: Error;
    /**
     * If we are rendering on the server
     */
    isServer: boolean;
    /**
     * The reference to the error
     */
    errorRef?: string;
};

const Error = ({ path, error, isServer, errorRef }: ErrorPageProps) => {
    const capturedError = error ?? {};

    // We don't have the locale here so we have to pull it from the url
    const [marketSlug] = path
        .replace(/^\//, '')
        .replace(/\/$/, '')
        .split('/') as unknown as MarketSlug;

    let activeMarketCode;

    try {
        // MarketSlug and MarketCode use the same key structure
        // the MarketSlug needs converting into a MarketCode
        // we can first get the MarketSlug key and use it to get the MarketCode value

        // get index from MarketSlug
        const marketSlugIndex = Object.values(MarketSlug).indexOf(
            marketSlug as MarketSlug
        );

        // get key of MarketSlug
        const marketSlugKey = Object.keys(MarketSlug)[marketSlugIndex];

        // use MarketSlug key to get MarketCode
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        const marketCode = MarketCode[marketSlugKey];

        if (marketCode) {
            activeMarketCode = marketCode;
        }
    } catch (error) {
        activeMarketCode = ukMarket.code;
    }

    // Object.prototype.toString:  https://262.ecma-international.org/5.1/#sec-15.2.4.2
    // returns "[object <class>]".
    // The class for Errors is Error: https://262.ecma-international.org/5.1/#sec-15.11.5
    function isError(error: unknown) {
        return Object.prototype.toString.call(error) === '[object Error]';
    }

    const hasError =
        isError(capturedError) || Object.keys(capturedError).length > 0;

    /**
     * Because errorRef is not always defined (when clientside errors occur), we
     * create a fallback ref and keep track of it (through re-renders).
     * This allows us to fall back to a ref and display it correctly to a user
     */
    const fallbackRef = useRef<string>();
    if (typeof window !== 'undefined' && !fallbackRef.current) {
        fallbackRef.current = uuid();
    }

    const trimmedError = (errorRef ?? fallbackRef.current ?? '').split('-')[0];

    /**
     * The following scenario only happens when we're on the server, where we
     * need to break the usual React render paradigm and run side effects in
     * the main function body... It's fine.
     *
     * NOTE: PLEASE DO NOT REMOVE THE WINDOW TYPE CHECK AS THIS SHOULD NEVER RUN
     * IN THE BROWSER!!
     */
    if (isServer && typeof window === 'undefined' && hasError) {
        (async () => {
            const { apm } = await import('@toucan/custom-server/elastic-apm');
            const logger = await import('@toucan/custom-server/logger');
            apm?.addLabels({ error_ref: errorRef, error_page_type: 'ssr' });
            apm?.captureError(capturedError);

            logger.default?.error?.(capturedError.message, {
                mpb: {
                    error_page_type: 'ssr',
                },
            });
        })();
    }

    /**
     * Send an error from the client on mount.
     * But don't send an SSR error (it will have already been captured above)
     */
    useEffect(() => {
        if (!isServer && hasError) {
            (async () => {
                const errorRefOrFallback =
                    errorRef ?? fallbackRef.current ?? '';

                window?.ElasticApmRum?.addLabels({
                    error_ref: errorRefOrFallback,
                    error_page_type: 'client',
                });

                window?.ElasticApmRum?.captureError(capturedError);

                await fetch('/elastic-apm-error', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        message: capturedError.message,
                        extra: {
                            error_ref: errorRefOrFallback,
                            mpb: {
                                error_page_type: 'client',
                            },
                        },
                    }),
                });
            })();
        }
    }, []);

    return (
        <Error500
            statusCode={500}
            refCode={trimmedError}
            activeMarketCode={activeMarketCode}
        />
    );
};

Error.getInitialProps = ({
    req,
    res,
    err,
}: {
    req: Request;
    res: Response;
    err: unknown;
}) => {
    const isServer = Boolean(res);
    const errorRef =
        // Can't use nullish coalescing as `false && undefined` evaluates to
        // false, meaning `uuid()` is not returned
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        (axios.isAxiosError(err) && err.response?.data?.error_ref) || uuid();

    const path = isServer ? req.originalUrl : window.location.pathname;

    const props = {
        isServer,
        error: err,
        path,
        errorRef,
    };

    return props;
};

export default Error;
